import { ProjectApi } from "@api/project-api/project-api";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { BaseProjectIdProps } from "@custom-types/sdb-company-types";
import {
  fetchAllCaptureTreeRevisions,
  fetchCaptureTree,
  fetchCaptureTreeForOpenDraftRevision,
} from "@store/capture-tree/capture-tree-thunks";
import { setFetchStatusCaptureTree } from "@store/capture-tree/capture-tree-slice";
import { store } from "@store/store";
import { AppDispatch, useAppDispatch } from "@store/store-helper";
import { useCallback, useEffect } from "react";
import { usePolling } from "@hooks/use-polling";
import { ProgressApiClient } from "@api/progress-api/progress-api-client";
import { useProgressApiClient } from "@api/progress-api/use-progress-api-client";
import { updateBackgroundTasks } from "@store/sdb-background-tasks/sdb-background-tasks-thunk";
import { resetSdbBackgroundTasksState } from "@store/sdb-background-tasks/sdb-background-tasks-slice";
import { isOpenDraftRevision } from "@utils/capture-tree/capture-tree-utils";
import { sentryCaptureError } from "@src/utils/sentry-utils";
import { CaptureTreeEntity } from "@faro-lotv/service-wires";
import { CaptureTreeRevision, CaptureTreeEntityWithRevisionStatus } from "@custom-types/capture-tree/capture-tree-types";
import { ISOTimeString } from "@faro-lotv/ielement-types";
import { FetchingStatus } from "@store/store-types";

/** Page size per API request. Since we fetch all tasks, use a higher value. */
const PAGE_SIZE_PROGRESS_API = 100;

/**
 * Custom hook that polls data required for the Data Management page
 */
export function useDataManagement({ projectId }: BaseProjectIdProps): void {
  const dispatch = useAppDispatch();
  const projectApiClient = useProjectApiClient({ projectId });
  const progressApiClient = useProgressApiClient({ projectId });

  /**
   * Fetches:
   * - The capture tree entities for the current main/draft revision of the current project
   * - The revisions of the current project
   * - The ProgressAPI tasks of the current project
   * Not currently done anymore (see comment):
   *   - The published ELS point cloud ielements, which is used to generate the PublishedData entities
   *   - The scan ielements and all their children ielements (PointCloudElsRaw, PointCloudE57, PointCloudStream, etc.)
   * The presence of the PointCloudStream as children of the scan indicates that the scan has been processed.
   *
   * @throws {error} if the requests to fetch capture tree data fail.
   */
  const fetchCaptureTreeDataCallback = useCallback(async (callCount: number): Promise<void> => {
    if (callCount === 0) {
      dispatch(setFetchStatusCaptureTree(FetchingStatus.pending));
    }
    let isSuccess = false;

    try {
      await fetchCaptureTreeData(dispatch, projectApiClient, progressApiClient);
      isSuccess = true;
    } finally {
      if (callCount === 0) {
        const status = isSuccess ? FetchingStatus.succeeded : FetchingStatus.rejected;
        dispatch(setFetchStatusCaptureTree(status));
      }
    }
  }, [dispatch, progressApiClient, projectApiClient]);

  /**
   * Error handler callback, if the 1st try fails.
   * We show an error page in this case, since we don't know the current state of the project.
   */
  const errorHandlerFirstLoad = useCallback((error: unknown): void => {
    sentryCaptureError({
      title: "CaptureTreeError: Failed to fetch capture tree data (on first load)",
      error,
    });
  }, []);

  usePolling({
    callback: fetchCaptureTreeDataCallback,
    errorHandlerFirstLoad,
    errorHandler: undefined,
    maxAttemptsMsg: "Failed to fetch the uploaded data for this project! Please reload the page and try again.",
  });

  /**
   * Resets the background tasks store on component unmount.
   * This is done to avoid any potential problem with the Project Activity tab, that tab shows all background tasks
   * and since we already fetched some tasks it might interfere with how pagination works in Activity tab.
   */
  useEffect(() => {
    // To avoid problems in the other direction (Activity -> Data Management), also reset the store on mount.
    dispatch(resetSdbBackgroundTasksState());

    return () => {
      dispatch(resetSdbBackgroundTasksState());
    };
  }, [dispatch]);
}

/** Result of fetchRevisionsAndDraftEntities(). */
export interface FetchRevisionsAndDraftEntitiesResult {
  revisions: CaptureTreeRevision[];
  openDraftRevision: CaptureTreeRevision | undefined;
  openDraftEntities: CaptureTreeEntityWithRevisionStatus[];
}

/** Result of fetchCaptureTreeData(). */
interface FetchCaptureTreeDataResult extends FetchRevisionsAndDraftEntitiesResult {
  mainEntities: CaptureTreeEntity[];
}

/** Slimmer version of fetchCaptureTreeData(), for main capture tree + tasks. */
async function fetchTasks(
  dispatch: AppDispatch,
  projectApiClient: ProjectApi,
  progressApiClient: ProgressApiClient
): Promise<void> {
  // Determine which tasks to fetch.
  // For the initial fetch, it would be more efficient to fetch only tasks related to the open draft revision.
  // But that doesn't work if there are older scans that had failed to process, and the draft was published
  // regardless of the failed processing.
  // Previous code: https://dev.azure.com/faro-connect/Stellar/_git/Dashboard/pullRequest/32853#1745937111
  let fromDate: ISOTimeString | undefined;
  if (store.getState().sdbBackgroundTasks.ids.length > 0) {
    // Follow-up fetch: Fetch tasks based on the last known update.
    // The tasks are updated using `sdbBackgroundTasksAdapter.upsertMany()`, so it is sufficient to
    // only fetch updates with a newer timestamp than the last known update.
    const lastUpdate = Object.values(store.getState().sdbBackgroundTasks.entities)
      .map((task) => new Date(task.updatedAt || task.createdAt).getTime())
      .reduce((lastUpdateAcc, taskUpdate) => {
        return taskUpdate > lastUpdateAcc ? taskUpdate : lastUpdateAcc;
      });
    // Add 1 msec to avoid fetching the last known update again.
    fromDate = new Date(lastUpdate + 1).toISOString();
  }

  // Fetch all background tasks, or all updates since the last known update.
  // A bit annoying, if we need it later:
  // - Promise returned by `dispatch` contains only the API response, without previous state.
  // - `store.getState().sdbBackgroundTasks.entities` was not yet updated when `dispatch` Promise is resolved.
  await dispatch(
    updateBackgroundTasks({
      progressApiClient,
      projectApiClient,
      fromDate,
      pageSize: PAGE_SIZE_PROGRESS_API,
      beforePage: null,
      shouldFetchAllPages: true,
      // We could set this, but it seems like we're already getting the last page, even when coming from the Activity tab.
      // See https://dev.azure.com/faro-connect/Stellar/_git/Dashboard/commit/5b7610fdbfa29a861021fb4628431472b2b83e4e
      //     https://dev.azure.com/faro-connect/Stellar/_git/Dashboard/pullrequest/26538
      // shouldOverrideBeforePage: true,
      shouldFetchIElements: false,
    })
  ).unwrap();
}

/** Slimmer version of fetchCaptureTreeData(), for revisions + draft entities. */
export async function fetchRevisionsAndDraftEntities(
  dispatch: AppDispatch,
  projectApiClient: ProjectApi,
  revisions?: CaptureTreeRevision[]
): Promise<FetchRevisionsAndDraftEntitiesResult> {
  revisions = revisions ?? await dispatch(
    fetchAllCaptureTreeRevisions({ projectApiClient })
  ).unwrap();

  const openDraftRevision = revisions.find(isOpenDraftRevision);
  const openDraftEntities = await dispatch(
    fetchCaptureTreeForOpenDraftRevision({
      projectApiClient,
      openDraftRevisionId: openDraftRevision?.id ?? null,
    })
  ).unwrap();

  return { revisions, openDraftRevision, openDraftEntities };
}

/**
 * Fetches the capture tree data required for the Data Management page.
 *
 * @param dispatch - The Redux dispatch function
 * @param projectApiClient - The project API client
 * @param progressApiClient - The progress API client
 */
export async function fetchCaptureTreeData(
  dispatch: AppDispatch,
  projectApiClient: ProjectApi,
  progressApiClient: ProgressApiClient
): Promise<FetchCaptureTreeDataResult> {
  const mainEntitiesPromise = dispatch(
    fetchCaptureTree({ projectApiClient })
  ).unwrap();

  const [mainEntities, revisionsDraft]  = await Promise.all([
    mainEntitiesPromise,
    fetchRevisionsAndDraftEntities(dispatch, projectApiClient),
    fetchTasks(dispatch, projectApiClient, progressApiClient),
  ]);

  const { revisions, openDraftRevision, openDraftEntities } = revisionsDraft;

  return {
    revisions,
    openDraftRevision,
    openDraftEntities,
    mainEntities,
  };
}
